import { useEffect, useState } from 'react';
import { Alert, Box, Drawer, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { RxCross2 } from 'react-icons/rx';
import Button from 'components/Common/Button';
import { useCreateA2pCampaign, useUpdateA2pCampaign } from '@/apis/business/setting/phoneNumbers/hook';
import { useGetBusinessProfile } from '@/apis/business/hook';
import { A2pBrand, A2pCampaign, A2pCampaignPayload } from '@/apis/business/setting/phoneNumbers/types';
import CampaignPaymentConfirmDialog from './CampaignPaymentConfirmDialog';
import { campaignDefaultsFromBusiness } from './businessFormPrefill';
import CampaignTypeStep from './CampaignTypeStep';
import ConsentDetailsStep from './ConsentDetailsStep';
import SampleMessagesStep from './SampleMessagesStep';
import AttributesStep from './AttributesStep';
import CompliancesStep from './CompliancesStep';
import {
  BORDER_COLOR,
  CAMPAIGN_STEPS,
  CampaignFormStep,
  CampaignFormValues,
  DEFAULT_CAMPAIGN_VALUES,
  DEFAULT_HELP_KEYWORDS,
  DEFAULT_OPTOUT_KEYWORDS,
  DEFAULT_USECASES,
  OptInMethod,
  brandHelpMessage,
  brandOptOutMessage,
  defaultCampaignName,
  defaultSampleMessages,
  outlinedPillSx,
  pillSx
} from './campaignFormShared';

type Props = {
  open: boolean;
  campaign?: A2pCampaign | null;
  brand?: A2pBrand | null;
  onClose: () => void;
};

const detailStr = (details: Record<string, unknown> | null | undefined, key: string) => {
  const value = details?.[key];
  return typeof value === 'string' ? value : '';
};

const parseUsecases = (
  campaign: A2pCampaign | null | undefined,
  details: Record<string, unknown> | null | undefined
): string[] => {
  const fromColumn = campaign?.usecases;
  if (Array.isArray(fromColumn) && fromColumn.length) return fromColumn.map(String).filter(Boolean);
  const raw = details?.usecases ?? details?.sub_usecases ?? details?.SubUsecases;
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw === 'string' && raw) return [raw];
  if (raw && typeof raw === 'object') {
    const inner =
      (raw as { SubUsecase?: unknown; sub_usecase?: unknown }).SubUsecase ??
      (raw as { sub_usecase?: unknown }).sub_usecase;
    if (Array.isArray(inner)) return inner.map(String).filter(Boolean);
    if (inner) return [String(inner)];
  }
  return [];
};

const detailBool = (details: Record<string, unknown> | null | undefined, key: string, fallback: boolean) => {
  const value = details?.[key];
  if (value === true || value === 'true' || value === 'TRUE') return true;
  if (value === false || value === 'false' || value === 'FALSE') return false;
  return fallback;
};

const apiErrorMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;

const toPayload = (values: CampaignFormValues, brandName: string): A2pCampaignPayload => {
  const campaignType = values.campaign_type || 'LOW_VOLUME';
  const name = values.name?.trim() || defaultCampaignName(brandName, campaignType);
  return {
    ...values,
    campaign_type: campaignType,
    name,
    description:
      values.description.trim() ||
      `${name} uses this campaign to send customer care and account notifications related to our products and services.`,
    sample1: values.sample1.trim(),
    sample2: values.sample2?.trim() || undefined,
    sample3: values.sample3?.trim() || undefined,
    sample4: values.sample4?.trim() || undefined,
    sample5: values.sample5?.trim() || undefined,
    message_flow:
      values.message_flow.trim() ||
      'Customers opt in through the website or verbal consent. This campaign sends customer care and account notifications. Reply STOP to unsubscribe and HELP for help.',
    help_message: values.help_message.trim() || brandHelpMessage(name),
    optout_message: values.optout_message.trim() || brandOptOutMessage(name),
    usecases: values.usecases?.length ? values.usecases : DEFAULT_USECASES
  };
};

export default function CampaignFormDrawer({ open, campaign = null, brand = null, onClose }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const primaryLight = theme.palette.primary.light || '#DCEEFF';
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<CampaignFormStep>('type');
  const [pendingPayload, setPendingPayload] = useState<A2pCampaignPayload | null>(null);
  const { mutateAsync: createCampaign, isPending: creating } = useCreateA2pCampaign();
  const { mutateAsync: updateCampaign, isPending: updating } = useUpdateA2pCampaign();
  const { data: businessProfile } = useGetBusinessProfile({ include: 'address,locations' });
  const isPending = creating || updating;
  const isEdit = Boolean(campaign?.id);
  const brandName = brand?.display_name || '';
  const brandEmail = typeof brand?.details?.email === 'string' ? brand.details.email : '';

  const { control, reset, watch, getValues, setValue, trigger } = useForm<CampaignFormValues>({
    defaultValues: DEFAULT_CAMPAIGN_VALUES
  });

  useEffect(() => {
    if (!open) return;
    setError(null);
    setPendingPayload(null);
    setStep('type');
    if (!campaign) {
      const defaults = campaignDefaultsFromBusiness(businessProfile);
      reset({
        ...DEFAULT_CAMPAIGN_VALUES,
        ...defaults,
        ...defaultSampleMessages(brandName),
        campaign_type: 'LOW_VOLUME',
        description: DEFAULT_CAMPAIGN_VALUES.description,
        message_flow: DEFAULT_CAMPAIGN_VALUES.message_flow,
        optout_keywords: DEFAULT_OPTOUT_KEYWORDS,
        help_keywords: DEFAULT_HELP_KEYWORDS,
        optout_message: defaults.optout_message || brandOptOutMessage(brandName),
        help_message: defaults.help_message || brandHelpMessage(brandName, brandEmail),
        privacy_policy_link: '',
        terms_and_conditions_link: '',
        usecases: DEFAULT_USECASES
      });
      return;
    }
    const details = campaign.details || {};
    reset({
      ...DEFAULT_CAMPAIGN_VALUES,
      name: campaign.name || '',
      campaign_type: campaign.campaign_type || 'LOW_VOLUME',
      vertical: campaign.vertical || detailStr(details, 'Vertical'),
      description: detailStr(details, 'Description') || campaign.name || '',
      sample1: detailStr(details, 'Sample1'),
      sample2: detailStr(details, 'Sample2'),
      sample3: detailStr(details, 'Sample3'),
      sample4: detailStr(details, 'Sample4'),
      sample5: detailStr(details, 'Sample5'),
      message_flow: detailStr(details, 'MessageFlow'),
      help_message: detailStr(details, 'HelpMessage') || brandHelpMessage(brandName, brandEmail),
      help_keywords: detailStr(details, 'HelpKeywords') || DEFAULT_HELP_KEYWORDS,
      optin_message: detailStr(details, 'OptinMessage'),
      optin_keywords: detailStr(details, 'OptinKeywords') || 'START,YES',
      optout_message: detailStr(details, 'OptoutMessage') || brandOptOutMessage(brandName),
      optout_keywords: detailStr(details, 'OptoutKeywords') || DEFAULT_OPTOUT_KEYWORDS,
      privacy_policy_link: detailStr(details, 'PrivacyPolicyLink'),
      terms_and_conditions_link: detailStr(details, 'TermsAndConditionsLink'),
      subscriber_opt_in: detailBool(details, 'SubscriberOptIn', true),
      subscriber_opt_out: detailBool(details, 'SubscriberOptOut', true),
      subscriber_help: detailBool(details, 'SubscriberHelp', true),
      embedded_link: detailBool(details, 'EmbeddedLink', false),
      embedded_phone: detailBool(details, 'EmbeddedPhone', false),
      number_pool: detailBool(details, 'NumberPool', false),
      age_gated: detailBool(details, 'AgeGated', false),
      direct_lending: detailBool(details, 'DirectLending', false),
      affiliate_marketing: detailBool(details, 'AffiliateMarketing', false),
      auto_renewal: detailBool(details, 'AutoRenewal', true),
      usecases: parseUsecases(campaign, details).length ? parseUsecases(campaign, details) : DEFAULT_USECASES,
      privacy_verified: detailBool(details, 'privacy_verified', Boolean(detailStr(details, 'PrivacyPolicyLink'))),
      terms_verified: detailBool(details, 'terms_verified', Boolean(detailStr(details, 'TermsAndConditionsLink'))),
      optin_method:
        ((detailStr(details, 'optin_method') || detailStr(details, 'OptinMethod')) as OptInMethod) || 'website_public',
      optin_page_url:
        detailStr(details, 'optin_page_url') ||
        detailStr(details, 'OptinPageUrl') ||
        DEFAULT_CAMPAIGN_VALUES.optin_page_url,
      optin_script:
        detailStr(details, 'optin_script') || detailStr(details, 'OptinScript') || DEFAULT_CAMPAIGN_VALUES.optin_script,
      optin_verified: detailBool(details, 'optin_verified', detailBool(details, 'OptinVerified', false)),
      pos_description:
        detailStr(details, 'pos_description') ||
        detailStr(details, 'PosDescription') ||
        DEFAULT_CAMPAIGN_VALUES.pos_description
    });
    // Prefill from business only when opening a new campaign
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaign, reset]);

  useEffect(() => {
    if (!open || campaign || !businessProfile) return;
    const defaults = campaignDefaultsFromBusiness(businessProfile);
    const current = getValues();
    (Object.keys(defaults) as (keyof typeof defaults)[]).forEach((key) => {
      const value = defaults[key];
      if (typeof value === 'string' && value && !String(current[key] || '').trim()) {
        setValue(key, value);
      }
    });
  }, [open, campaign, businessProfile, getValues, setValue]);

  const stepIndex = CAMPAIGN_STEPS.findIndex((item) => item.id === step);

  const goNext = async () => {
    setError(null);
    if (step === 'type') {
      if (!getValues('campaign_type')) {
        setError('Select a campaign type to continue.');
        return;
      }
      setStep('consent');
      return;
    }
    if (step === 'consent') {
      const method = getValues('optin_method');
      const fields: Array<keyof CampaignFormValues> = ['description', 'vertical', 'usecases', 'message_flow'];
      if (method !== 'verbal') fields.push('optin_page_url');
      if (method === 'verbal') fields.push('optin_script');
      if (method === 'pos') fields.push('pos_description');
      const valid = await trigger(fields);
      if (!valid) return;
      if ((getValues('usecases') || []).length < 2) {
        setError('Select at least 2 use cases.');
        return;
      }
      if (!getValues('optin_verified')) {
        setError('Verify your Messaging Opt-In Agreement to continue.');
        return;
      }
      setStep('samples');
      return;
    }
    if (step === 'samples') {
      const valid = await trigger(['sample1', 'sample2']);
      if (!valid) return;
      setStep('attributes');
      return;
    }
    if (step === 'attributes') {
      const values = getValues();
      const fields: Array<keyof CampaignFormValues> = [];
      if (values.subscriber_opt_out) fields.push('optout_message');
      if (values.subscriber_help) fields.push('help_message');
      const valid = fields.length ? await trigger(fields) : true;
      if (!valid) return;
      setStep('compliances');
    }
  };

  const goBack = () => {
    setError(null);
    if (step === 'consent') setStep('type');
    if (step === 'samples') setStep('consent');
    if (step === 'attributes') setStep('samples');
    if (step === 'compliances') setStep('attributes');
  };

  const onSubmit = async () => {
    setError(null);
    const valid = await trigger(['privacy_policy_link', 'terms_and_conditions_link']);
    if (!valid) return;
    const values = getValues();
    if (!values.privacy_verified || !values.terms_verified) {
      setError('Verify that your Privacy Policy and Terms & Conditions contain the required disclosures.');
      return;
    }
    setPendingPayload(toPayload(values, brandName));
  };

  const confirmPayment = async () => {
    if (!pendingPayload) return;
    try {
      if (isEdit) {
        await updateCampaign({ id: campaign!.id, payload: { a2p_campaign: pendingPayload } });
      } else {
        await createCampaign({ a2p_campaign: pendingPayload });
      }
      setPendingPayload(null);
      onClose();
    } catch (err) {
      setPendingPayload(null);
      setError(apiErrorMessage(err, 'Unable to save campaign. Please check the fields and try again.'));
    }
  };

  const stepProps = { control, watch, setValue, lockType: isEdit, lockBooleans: isEdit, brandName };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 900,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2.5}
        borderBottom={`1px solid ${BORDER_COLOR}`}
      >
        <Typography fontSize={18} fontWeight={500} color='text.primary'>
          {isEdit ? 'Edit A2P Campaign' : 'Register A2P Campaign'}
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close' sx={{ color: 'text.primary' }}>
          <RxCross2 size={20} />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={3} py={3}>
        <Box
          display='grid'
          gridTemplateColumns='repeat(5, minmax(0, 1fr))'
          gap={0.5}
          p={0.5}
          border={`1px solid ${BORDER_COLOR}`}
          borderRadius='8px'
          mb={3}
        >
          {CAMPAIGN_STEPS.map((item) => {
            const active = step === item.id;
            return (
              <Box
                key={item.id}
                onClick={() => setStep(item.id)}
                py={1.1}
                px={0.75}
                borderRadius='6px'
                bgcolor={active ? primaryLight : 'transparent'}
                sx={{ cursor: 'pointer' }}
              >
                <Typography
                  fontSize={13}
                  fontWeight={500}
                  textAlign='center'
                  noWrap
                  color={active ? 'text.primary' : 'text.secondary'}
                >
                  {item.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {error && (
          <Alert severity='error' onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === 'type' && <CampaignTypeStep {...stepProps} />}
        {step === 'consent' && <ConsentDetailsStep {...stepProps} />}
        {step === 'samples' && <SampleMessagesStep {...stepProps} brandName={brandName} />}
        {step === 'attributes' && <AttributesStep {...stepProps} />}
        {step === 'compliances' && <CompliancesStep {...stepProps} />}
      </Box>

      <Box display='flex' justifyContent='flex-end' gap={1.5} px={3} py={2} borderTop={`1px solid ${BORDER_COLOR}`}>
        {stepIndex === 0 ? (
          <Button variant='outlined' onClick={onClose} disabled={isPending} sx={{ ...outlinedPillSx, minWidth: 80 }}>
            Cancel
          </Button>
        ) : (
          <Button variant='outlined' onClick={goBack} disabled={isPending} sx={{ ...outlinedPillSx, minWidth: 80 }}>
            Back
          </Button>
        )}
        {step === 'compliances' ? (
          <Button variant='contained' color='primary' onClick={() => void onSubmit()} disabled={isPending} sx={pillSx}>
            {isPending ? 'Saving…' : 'Submit Campaign'}
          </Button>
        ) : (
          <Button
            variant='contained'
            color='primary'
            onClick={() => void goNext()}
            disabled={isPending}
            sx={{ ...pillSx, minWidth: 80 }}
          >
            Next
          </Button>
        )}
      </Box>

      <CampaignPaymentConfirmDialog
        open={Boolean(pendingPayload)}
        usecase={pendingPayload?.campaign_type ?? ''}
        isResubmit={isEdit}
        isPending={isPending}
        onCancel={() => setPendingPayload(null)}
        onConfirm={() => void confirmPayment()}
      />
    </Drawer>
  );
}
